import { useState } from "react";
import { ShieldCheck, Sparkles, Globe, BadgeCheck, ScrollText, RefreshCw, BrainCircuit, HelpCircle, X } from "lucide-react";
import { computeTrust, LAST_VERIFIED, TRUST_FACTORS } from "@/lib/trust";

const SEAL = "/brand/verified-seal.png";
const CAMPUS = "/brand/campus-line.png";

const CHIPS = [
  { icon: Globe, label: "Official Website Verified" },
  { icon: BrainCircuit, label: "AI Intelligence Checked" },
  { icon: BadgeCheck, label: "Institution Identity Confirmed" },
  { icon: ScrollText, label: "Academic Information Audited" },
  { icon: RefreshCw, label: "Regularly Updated" },
];

function Seal({ className = "" }) {
  return (
    <div className={`relative shrink-0 ${className}`}>
      <div className="absolute inset-0 rounded-full bg-[#D4A24C]/30 blur-xl" />
      <img src={SEAL} alt="CollegeUnlock Verified seal" className="relative h-full w-full object-contain drop-shadow-[0_6px_14px_rgba(0,0,0,0.45)]" />
    </div>
  );
}

function Gauge({ score, label, size = 92, onInfo }) {
  const inner = size - 26;
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{ width: size, height: size, background: `conic-gradient(#E9C877 ${score * 3.6}deg, rgba(255,255,255,0.12) 0deg)` }}
      >
        <div className="flex flex-col items-center justify-center rounded-full bg-[#0B2352] ring-1 ring-white/10" style={{ width: inner, height: inner }}>
          <span className="font-display text-lg font-extrabold text-white leading-none">{score}%</span>
          <span className="mt-0.5 text-[8px] font-semibold uppercase tracking-wider text-[#E9C877]">Trust</span>
        </div>
      </div>
      <span className="mt-2 text-[11px] font-semibold text-white/80">{label}</span>
      <button type="button" onClick={onInfo} data-testid="trust-info-btn" className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-white/45 underline decoration-white/20 underline-offset-2 hover:text-white/70">
        <HelpCircle className="h-3 w-3" /> How is this calculated?
      </button>
    </div>
  );
}

function TrustModal({ score, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="trust-info-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h4 className="font-display text-lg font-bold text-slate-900">CollegeUnlock Trust Score</h4>
          <button onClick={onClose} data-testid="trust-info-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <p className="mt-2 text-sm text-slate-600">The Trust Score (85–98) reflects <b>CollegeUnlock's confidence</b> in the institutional data shown here. It is computed from verification signals below — it is <b>not</b> an official accreditation or ranking.</p>
        <ul className="mt-4 space-y-2">
          {TRUST_FACTORS.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-slate-400">Detailed methodology will be published soon. This institution scores <b className="text-[#9a6b1e]">{score}%</b>.</p>
      </div>
    </div>
  );
}

function Wordmark({ small = false }) {
  return (
    <div className="leading-tight">
      <p className={`cu-gold-text font-display font-bold tracking-[0.13em] opacity-90 ${small ? "text-[11px]" : "text-xs"}`}>COLLEGEUNLOCK VERIFIED</p>
      <p className="mt-0.5 text-[9.5px] font-medium text-white/50">Machine Verified · Official Data Sources · AI Reviewed</p>
    </div>
  );
}

function Chips() {
  return (
    <div className="flex flex-wrap gap-1.5">
      {CHIPS.map((c) => (
        <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A24C]/35 bg-white/[0.05] px-2.5 py-1 text-[11px] font-medium text-white/90">
          <c.icon className="h-3 w-3 text-[#E9C877]" /> {c.label}
        </span>
      ))}
    </div>
  );
}

function Footer() {
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-3">
      <span className="text-[11px] text-white/55">Last Verified <span className="font-semibold text-white/80">{LAST_VERIFIED}</span> · Auto-updated</span>
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/55">
        <Sparkles className="h-3 w-3 text-[#E9C877]" /> Verified by <span className="cu-gold-text font-semibold">CollegeUnlock Intelligence™</span>
      </span>
    </div>
  );
}

const shell = "relative overflow-hidden rounded-2xl border border-[#D4A24C]/25 bg-gradient-to-br from-[#0B2352]/95 to-[#123072]/95 backdrop-blur-xl shadow-[0_30px_80px_-24px_rgba(3,10,35,0.85)] vih-sweep";

// Premium "CollegeUnlock Verified" trust card. variant: "certificate" | "wallet".
export default function VerifiedInstitutionHero({ name, facts = [], signals = {}, variant = "certificate", className = "" }) {
  const { score, label } = computeTrust(signals, name);
  const [info, setInfo] = useState(false);
  const factLine = facts.filter(Boolean).map((f) => f.value).slice(0, 3).join(" · ");

  if (variant === "wallet") {
    return (
      <div data-testid="verified-institution-hero" data-variant="wallet" className={`${shell} p-6 sm:p-7 ${className}`}>
        {info && <TrustModal score={score} onClose={() => setInfo(false)} />}
        <img src={CAMPUS} alt="" aria-hidden className="pointer-events-none absolute -bottom-8 -right-6 w-52 opacity-[0.07]" />
        <div className="relative">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3"><Seal className="h-14 w-14" /><Wordmark /></div>
            <Gauge score={score} label={label} size={82} onInfo={() => setInfo(true)} />
          </div>
          <h3 className="mt-4 font-display text-xl font-bold leading-snug text-white line-clamp-2">{name}</h3>
          {factLine && <p className="mt-1 text-xs text-white/50">{factLine}</p>}
          <div className="mt-4"><Chips /></div>
          <Footer />
        </div>
      </div>
    );
  }

  // certificate (recommended flagship)
  return (
    <div data-testid="verified-institution-hero" data-variant="certificate" className={`${shell} p-6 sm:p-7 ${className}`}>
      {info && <TrustModal score={score} onClose={() => setInfo(false)} />}
      <img src={CAMPUS} alt="" aria-hidden className="pointer-events-none absolute inset-x-0 -bottom-6 mx-auto w-64 opacity-[0.06]" />
      <div className="pointer-events-none absolute -top-20 -right-16 h-52 w-52 rounded-full bg-[#D4A24C]/15 blur-3xl" />
      <div className="relative flex flex-col items-center text-center">
        <Seal className="h-16 w-16 sm:h-[72px] sm:w-[72px]" />
        <div className="mt-2.5"><Wordmark /></div>
        <h3 className="mt-3 font-display text-2xl font-extrabold leading-tight text-white line-clamp-2">{name}</h3>
        {factLine && <p className="mt-1.5 text-xs text-white/55">{factLine}</p>}
        <div className="mt-6"><Gauge score={score} label={label} onInfo={() => setInfo(true)} /></div>
        <div className="mt-6 w-full"><Chips /></div>
        <div className="w-full"><Footer /></div>
      </div>
    </div>
  );
}
